import { useEffect, useRef, useState } from "react";
import type { WorkflowRunView } from "@dentacore/shared-types";

import { answerRingRun } from "../../api/workflows";
import type { RingAction } from "./ring-registry";
import styles from "./ring-run-panel.module.css";

/**
 * **A workflow run from the ring** (WF-D3, STORY-225). Steps that run on their
 * own have already gone through on the server; a step someone does is asked
 * for here — *Open it* presses the thing's own ring action (its dialog), and
 * *Done* or *Skip* tells the run. The panel says where it finished, or why it
 * stopped, and keeps the current step in view however many there are (WF-D10).
 *
 * It sits in a corner rather than over the page, so the dialog a step opens is
 * usable beside it.
 */

/** A workflow's step names the catalogue action; the ring may call the same button by its own key. */
const RING_ALIASES: Record<string, string[]> = {
  complete: ["step"],
  next: ["next", "book"],
  book: ["book", "next"],
};

export function RingRunPanel({
  run,
  actions,
  onChange,
  onClose,
}: {
  run: WorkflowRunView;
  /** The ring's own actions on the thing the run started on. */
  actions: RingAction[];
  onChange: (run: WorkflowRunView) => void;
  onClose: () => void;
}): React.JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const list = useRef<HTMLOListElement>(null);

  useEffect(() => {
    list.current?.lastElementChild?.scrollIntoView({ block: "nearest" });
  }, [run.steps.length, run.asking]);

  const asking = run.asking;
  const shortcut =
    asking === null
      ? undefined
      : actions.find((a) =>
          [asking.action, ...(RING_ALIASES[asking.action] ?? [])].includes(a.key),
        );

  const answer = (reply: "done" | "skip") => {
    setBusy(true);
    setError(null);
    answerRingRun(run.id, reply)
      .then(onChange)
      .catch((caught: unknown) =>
        setError(caught instanceof Error ? caught.message : "That didn't go through"),
      )
      .finally(() => setBusy(false));
  };

  const finished =
    run.status === "DONE" ||
    run.status === "STOPPED" ||
    run.status === "FAILED" ||
    run.status === "CANCELLED";

  return (
    <section
      className={styles.panel}
      aria-label={`Workflow ${run.workflowName}`}
      data-testid="ring-run"
    >
      <header className={styles.head}>
        <div>
          <h2 className={styles.title}>{run.workflowName}</h2>
          <p className={styles.record}>{run.recordName}</p>
        </div>
        <button
          type="button"
          className={styles.close}
          onClick={onClose}
          aria-label="Close the workflow panel"
        >
          ×
        </button>
      </header>

      <ol ref={list} className={styles.steps}>
        {run.steps.map((step, i) => (
          <li key={`${step.nodeId}-${String(i)}`} data-result={step.result}>
            <span className={styles.label}>{step.label === "" ? step.message : step.label}</span>
            {step.label !== "" && <span className={styles.message}>{step.message}</span>}
          </li>
        ))}
      </ol>

      {asking !== null && !finished && (
        <div className={styles.ask} data-testid="ring-run-ask">
          <p className={styles.turn}>
            Your turn: <strong>{asking.label}</strong>
          </p>
          <div className={styles.buttons}>
            {shortcut !== undefined && (
              <button type="button" onClick={() => shortcut.run()} data-testid="ring-run-open">
                Open it
              </button>
            )}
            <button
              type="button"
              className={styles.primary}
              disabled={busy}
              onClick={() => answer("done")}
              data-testid="ring-run-done"
            >
              Done
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => answer("skip")}
              data-testid="ring-run-skip"
            >
              Skip
            </button>
          </div>
          {shortcut === undefined && (
            <p className={styles.hint}>Do it on the screen, then press Done.</p>
          )}
        </div>
      )}

      {finished && (
        <p className={styles.outcome} data-status={run.status} data-testid="ring-run-outcome">
          {run.status === "DONE"
            ? "Finished"
            : run.status === "CANCELLED"
              ? "Cancelled"
              : "Stopped"}
          {run.outcome !== null && ` — ${run.outcome}`}
        </p>
      )}
      {error !== null && <p className={styles.error}>{error}</p>}
    </section>
  );
}
